import React, { useCallback, useMemo, useState } from 'react';
import { Flex, Box, Image } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { iNavigation } from '../../../common/interfaces/index';
import useLinks from '../../../common/hooks/useLinks/useLinks';
import { useSidebar } from './useSidebar';
import { IconChevron } from '../../components/Icons/Icons';
import arrowRightIcon from '../../../assets/icons/arrow-right-gray.svg';
import EnvironmentBadge from './EnvironmentBadge/EnvironmentBadge';
import './Sidebar.scss';

export interface SidebarProps {
  navigationItems?: iNavigation[];
  className?: string;
}

const getNavItemKey = (item: iNavigation, index: number): string =>
  item.pathKey || String(item.id ?? index);

const Sidebar: React.FC<SidebarProps> = ({
  navigationItems: customNavigationItems,
  className,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const linksFromHook = useLinks();
  const navigationList = customNavigationItems || linksFromHook;

  const {
    isVisuallyExpanded,
    isHovering,
    isOpenMenu,
    handleToggleMenu,
    handleMouseEnter,
    handleMouseLeave,
    handleItemClick,
    isItemActive,
  } = useSidebar();

  const [expandedOverrides, setExpandedOverrides] = useState<
    Record<string, boolean>
  >({});

  const defaultExpandedKeys = useMemo(() => {
    const keys: Record<string, boolean> = {};
    navigationList.forEach((item, index) => {
      if (!item.children?.length) {
        return;
      }
      const itemKey = getNavItemKey(item, index);
      const childActive = item.children.some(child => isItemActive(child));
      const parentActive = isItemActive(item);
      if (childActive || parentActive) {
        keys[itemKey] = true;
      }
    });
    return keys;
  }, [navigationList, isItemActive, pathname]);

  const isItemExpanded = useCallback(
    (itemKey: string) => {
      if (itemKey in expandedOverrides) {
        return expandedOverrides[itemKey];
      }
      return defaultExpandedKeys[itemKey] ?? false;
    },
    [defaultExpandedKeys, expandedOverrides],
  );

  const toggleExpanded = useCallback((itemKey: string) => {
    setExpandedOverrides(prev => {
      const currentlyExpanded =
        itemKey in prev ? prev[itemKey] : defaultExpandedKeys[itemKey] ?? false;
      return { ...prev, [itemKey]: !currentlyExpanded };
    });
  }, [defaultExpandedKeys]);

  return (
    <Flex
      id='sidebar'
      direction='column'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classNames(
        {
          'navigation-side': true,
          'navigation-side__open': isVisuallyExpanded,
          'navigation-side__short': !isVisuallyExpanded,
          'navigation-side__hover': isHovering && !isOpenMenu,
        },
        className,
      )}
    >
      <EnvironmentBadge className='mt-20' visible={isVisuallyExpanded} />
      <Flex
        width='full'
        className='navigation-side__nav-container'
        justifyContent='flex-start'
        flexDirection='column'
        flex='1'
        overflow='hidden'
      >
        <Box className='navigation-side__menu-toggle-section'>
          <a
            href='#'
            onClick={handleToggleMenu}
            className='navigation-side__menu-toggle-link'
          >
            <span className='navigation-side__nav-icon'>
              <Image
                src={arrowRightIcon}
                alt='toggle'
                width='16px'
                height='16px'
                transform={
                  isVisuallyExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                }
                display='inline-block'
              />
            </span>
            <span
              className={classNames(
                'navigation-side__menu-toggle-label',
                'text-uppercase',
                { 'navigation-side__menu-toggle-label--collapsed': !isVisuallyExpanded },
              )}
            >
              {t('navigation.title')}
            </span>
          </a>
        </Box>

        <Box
          className='navigation-side__menu-section scrollbar'
          flex='1'
          overflowY='auto'
          overflowX='hidden'
        >
          <ul className='navigation-side__nav'>
            {navigationList.map((item: iNavigation, index: number) => {
              const active = isItemActive(item);
              const hasChildren = Boolean(item.children?.length);
              const itemKey = getNavItemKey(item, index);
              const isExpanded = isItemExpanded(itemKey);

              return (
                <li
                  key={item.path || item.route || index}
                  className={classNames('navigation-side__nav-item', {
                    'navigation-side__nav-item--active': active && !hasChildren,
                    'navigation-side__nav-item--expandable': hasChildren,
                    'navigation-side__nav-item--expanded': isExpanded,
                  })}
                >
                  {hasChildren ? (
                    <button
                      type='button'
                      className='navigation-side__nav-link navigation-side__nav-link--parent'
                      aria-expanded={isExpanded}
                      onClick={() => toggleExpanded(itemKey)}
                    >
                      <div className='navigation-side__nav-link-main'>
                        {item.icon && (
                          <span className='navigation-side__nav-icon'>
                            {item.icon}
                          </span>
                        )}
                        {isVisuallyExpanded && (
                          <span className='navigation-side__nav-text'>
                            {item.title}
                          </span>
                        )}
                      </div>
                      {isVisuallyExpanded && (
                        <span
                          className='navigation-side__nav-chevron'
                          aria-hidden='true'
                        >
                          <IconChevron isOpen={isExpanded} isRelative />
                        </span>
                      )}
                    </button>
                  ) : (
                    <a
                      href={item.path || item.route}
                      onClick={e => {
                        e.preventDefault();
                        handleItemClick(item);
                      }}
                      className={classNames('navigation-side__nav-link', {
                        active,
                      })}
                    >
                      {item.icon && (
                        <span className='navigation-side__nav-icon'>
                          {item.icon}
                        </span>
                      )}
                      {isVisuallyExpanded && (
                        <span className='navigation-side__nav-text'>
                          {item.title}
                        </span>
                      )}
                    </a>
                  )}

                  {hasChildren && isVisuallyExpanded && isExpanded && (
                    <ul className='navigation-side__nav-children'>
                      {item.children!.map((child: iNavigation, childIndex) => {
                        const childKey =
                          child.pathKey ||
                          child.path ||
                          child.route ||
                          `${itemKey}-${childIndex}`;
                        const childActive = isItemActive(child);

                        return (
                          <li
                            key={childKey}
                            className={classNames(
                              'navigation-side__nav-item navigation-side__nav-item--child',
                              {
                                'navigation-side__nav-item--child-active':
                                  childActive,
                              },
                            )}
                          >
                            <button
                              type='button'
                              className={classNames(
                                'navigation-side__nav-link navigation-side__nav-link--child',
                                { active: childActive },
                              )}
                              onClick={e => {
                                e.stopPropagation();
                                handleItemClick(child);
                              }}
                            >
                              <span className='navigation-side__nav-text'>
                                {child.title}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </Box>
      </Flex>
    </Flex>
  );
};

export default Sidebar;
